/**
 * The MTC guide, bound as a book. The section renders every spread stacked (the
 * no-JS order); this script binds them into a codex from 990px and a hanging
 * scroll below. The mode class is stamped here, so a missing asset leaves the
 * stacked page rather than a book that cannot open.
 *
 * Contracts with bq-mtc.liquid:
 * - [data-book-spread]  pages in reading order; ids are the public anchors
 *   (#mtc-chapN) the editorial tiles and Nuwa deep-link to.
 * - [data-book-goto]    seals and table rows: absolute page index.
 * - [data-book-dir]     arrows and page-edge zones: plus or minus one.
 * - [data-book-exit]    leaves the fullscreen reading room.
 * - --bq-adv            0..1 reading advance; CSS derives the fore-edge stacks.
 */
(function () {
  var bq = window.bq;
  if (!bq) return;
  var SINGLE = matchMedia('(max-width: 989px)');

  /* Lettering */
  // Split a block's text nodes into per-character spans, once. The block
  // keeps its full text as aria-label and the characters leave the
  // accessibility tree: a screen reader hears words, never spelling. Anchors
  // are left whole so their names survive.
  function splitBlock(el) {
    if (el.dataset.bqTw) return;
    el.dataset.bqTw = '1';
    var label = el.textContent;
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return node.parentElement && node.parentElement.closest('a')
          ? NodeFilter.FILTER_REJECT
          : NodeFilter.FILTER_ACCEPT;
      },
    });
    var textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(function (node) {
      var frag = document.createDocumentFragment();
      var text = node.nodeValue;
      for (var i = 0; i < text.length; i++) {
        var ch = text[i];
        if (/\s/.test(ch)) {
          frag.appendChild(document.createTextNode(ch));
        } else {
          var s = document.createElement('span');
          s.className = 'bq-tw';
          s.setAttribute('aria-hidden', 'true');
          s.textContent = ch;
          frag.appendChild(s);
        }
      }
      node.parentNode.replaceChild(frag, node);
    });
    el.setAttribute('aria-label', label);
  }

  var TW_TARGETS =
    '.bq-mtc__chap-title, .bq-mtc__prose p, .bq-book__front-title, ' +
    '.bq-book__front-intro p, .bq-cta__title, .bq-cta__text';

  // Deal the whole page one glyph stream: ~0.5ms per character, so a full
  // chapter writes itself in about a second, quick enough to chase.
  function typeIn(spread) {
    if (bq.reducedMotion()) return;
    spread.querySelectorAll(TW_TARGETS).forEach(splitBlock);
    var chars = spread.querySelectorAll('.bq-tw');
    chars.forEach(function (c, i) { c.style.setProperty('--tw', i); });
    spread.classList.remove('is-typing');
    void spread.offsetWidth; // restart the animation on every arrival
    spread.classList.add('is-typing');
    clearTimeout(spread.bqTwTimer);
    spread.bqTwTimer = setTimeout(function () {
      spread.classList.remove('is-typing');
    }, chars.length * 0.5 + 400);
  }

  /* The codex */
  function initCodex(root) {
    root.classList.add('bq-mtc--bound');

    var vol = root.querySelector('[data-book]');
    var cover = root.querySelector('[data-book-cover]');
    var live = root.querySelector('[data-book-live]');
    var spreads = Array.prototype.slice.call(root.querySelectorAll('[data-book-spread]'));
    if (!vol || !cover || !spreads.length) return;

    var LAST = spreads.length - 1;
    var rtl = (document.documentElement.dir || '').toLowerCase() === 'rtl';
    var idx = 0;
    var opened = false;
    var lastTrigger = null;

    var byId = {};
    spreads.forEach(function (s, k) { byId[s.id] = k; });

    function present(i, opts) {
      opts = opts || {};
      idx = i;
      spreads.forEach(function (s, k) {
        var cur = k === i;
        s.classList.toggle('is-current', cur);
        s.inert = !cur;
        if (cur) s.scrollTop = 0; // a page is read from its head
      });
      vol.style.setProperty('--bq-adv', LAST ? (i / LAST).toFixed(3) : '0');
      root.querySelectorAll('[data-book-goto]').forEach(function (b) {
        var cur = parseInt(b.getAttribute('data-book-goto'), 10) === i;
        b.classList.toggle('is-current', cur);
        if (cur) b.setAttribute('aria-current', 'true');
        else b.removeAttribute('aria-current');
      });
      root.querySelectorAll('[data-book-dir]').forEach(function (b) {
        var d = parseInt(b.getAttribute('data-book-dir'), 10);
        b.disabled = d < 0 ? i === 0 : i === LAST;
      });
      // replaceState, not pushState: leafing through is reading, not
      // navigating: Back must leave the page, not retrace six turns.
      if (opts.hash !== false) history.replaceState(null, '', '#' + spreads[i].id);
      if (live) live.textContent = spreads[i].getAttribute('aria-label') || '';
      typeIn(spreads[i]);
    }

    function goTo(i) {
      if (i === idx && opened) return;
      if (i < 0 || i > LAST) return;
      if (!opened) { openBook(i, true); return; }
      present(i);
    }

    function openBook(target, jump) {
      if (opened) { goTo(target); return; }
      opened = true;
      // The volume widens as it opens; freeze the board's width in px so the
      // rotating face is not stretched mid-flight by its 50%-of-vol basis.
      cover.style.width = cover.offsetWidth + 'px';
      root.classList.add('is-open');
      root.classList.add('is-fs');
      document.body.classList.add('bq-book-reading');
      present(target, { hash: target !== 0 });
      var done = false;
      var away = function () {
        if (done) return;
        done = true;
        cover.hidden = true;
      };
      if (jump || bq.reducedMotion()) {
        away();
      } else {
        cover.addEventListener('transitionend', function h(e) {
          if (e.propertyName !== 'transform') return;
          cover.removeEventListener('transitionend', h);
          away();
        });
        // transitionend is lost to a background tab: never leave the board up.
        setTimeout(away, 1700);
      }
      vol.setAttribute('tabindex', '-1');
      vol.focus({ preventScroll: true });
    }

    // Out of the reading room: the book closes back onto the desk, the hash
    // clears, the page scrolls again.
    function closeBook() {
      if (!opened) return;
      opened = false;
      root.classList.remove('is-open', 'is-fs');
      document.body.classList.remove('bq-book-reading');
      // The reading room scrolls its own stage; back to overflow:hidden that
      // leftover scrollTop survives and shears the closed book off-frame.
      var stage = root.querySelector('.bq-book__stage');
      if (stage) stage.scrollTop = 0;
      cover.hidden = false;
      cover.style.width = '';
      history.replaceState(null, '', location.pathname + location.search);
      // openBook() moved focus into the volume; hand it back to whatever
      // opened the book, else it falls to the top of the document.
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus({ preventScroll: true });
      }
      lastTrigger = null;
    }

    var h0 = byId[decodeURIComponent(location.hash).slice(1)];
    if (typeof h0 === 'number') openBook(h0, true);
    // Bound outside the section, so it outlives it: an editor re-render leaves
    // this binding behind on a detached root, so it is dropped on first call.
    addEventListener('hashchange', function onHash() {
      if (!root.isConnected) { removeEventListener('hashchange', onHash); return; }
      var t = byId[decodeURIComponent(location.hash).slice(1)];
      if (typeof t !== 'number') return;
      if (!opened) openBook(t, false);
      else goTo(t);
    });

    root.addEventListener('click', function (e) {
      // The cover face and the cover TOC rows both open the book.
      if (!opened) lastTrigger = e.target.closest('[data-book-open], [data-book-goto]');
      if (e.target.closest('[data-book-open]')) { openBook(0, false); return; }
      if (e.target.closest('[data-book-exit]')) { closeBook(); return; }
      var go = e.target.closest('[data-book-goto]');
      if (go) {
        e.preventDefault(); // TOC rows are real #mtc-* anchors for the no-JS path
        goTo(parseInt(go.getAttribute('data-book-goto'), 10));
        return;
      }
      var dir = e.target.closest('[data-book-dir]');
      if (dir && !dir.disabled) goTo(idx + parseInt(dir.getAttribute('data-book-dir'), 10));
    });

    // Arrow keys leaf through; Escape leaves the room. "Right" advances in
    // LTR and goes back in RTL: the keys follow the page, not the index.
    document.addEventListener('keydown', function onKey(e) {
      if (!root.isConnected) { document.removeEventListener('keydown', onKey); return; }
      if (!opened) return;
      if (e.key === 'Escape') { closeBook(); return; }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      var t = e.target;
      if (t && t.closest && t.closest('input, textarea, select, [contenteditable]')) return;
      var forward = (e.key === 'ArrowRight') !== rtl;
      goTo(idx + (forward ? 1 : -1));
    });
  }

  /* The scroll */
  function initRoll(root) {
    root.classList.add('bq-mtc--roll');
    var spreads = Array.prototype.slice.call(root.querySelectorAll('[data-book-spread]'));
    var nav = root.querySelector('.bq-book__nav');
    if (!spreads.length || !nav) return;

    function jump(i) {
      var target = spreads[i];
      if (!target) return;
      var doux = !bq.reducedMotion();
      var top = target.getBoundingClientRect().top + window.scrollY - nav.offsetHeight - 8;
      window.scrollTo({ top: top, behavior: doux ? 'smooth' : 'auto' });
      history.replaceState(null, '', '#' + target.id);
      // The layout can move under the flight (the scroll-up header surfacing,
      // a phone's URL bar folding, late heights settling), and the landing
      // then shows the tail of the previous leaf. One silent re-aim from the
      // real geometry, once the dust falls.
      setTimeout(function () {
        var ecart = target.getBoundingClientRect().top - nav.offsetHeight - 8;
        if (Math.abs(ecart) > 4) window.scrollBy({ top: ecart, behavior: 'auto' });
      }, doux ? 750 : 120);
    }

    root.addEventListener('click', function (e) {
      var go = e.target.closest('[data-book-goto]');
      if (!go) return;
      e.preventDefault();
      jump(parseInt(go.getAttribute('data-book-goto'), 10));
    });

    // Light spy: the seal of the chapter under the eye holds the gold. The
    // rail recentres by scrolling itself only: scrollIntoView walks every
    // scrollable ancestor, and the sticky rail's flow box lives at the top of
    // the stage, so each firing yanked the whole page back up there.
    if ('IntersectionObserver' in window) {
      var list = root.querySelector('.bq-book__seals');
      var seals = {};
      var courant = -1;
      root.querySelectorAll('.bq-book__seal[data-book-goto]').forEach(function (b) {
        seals[parseInt(b.getAttribute('data-book-goto'), 10)] = b;
      });
      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          var i = spreads.indexOf(en.target);
          if (i === courant) return;
          courant = i;
          Object.keys(seals).forEach(function (k) {
            var cur = parseInt(k, 10) === i;
            seals[k].classList.toggle('is-current', cur);
            if (cur) seals[k].setAttribute('aria-current', 'true');
            else seals[k].removeAttribute('aria-current');
          });
          var b = seals[i];
          if (b && list) {
            var lr = list.getBoundingClientRect();
            var br = b.getBoundingClientRect();
            var gauche = list.scrollLeft + (br.left - lr.left) - (list.clientWidth - br.width) / 2;
            list.scrollTo({ left: Math.max(0, gauche), behavior: bq.reducedMotion() ? 'auto' : 'smooth' });
          }
        });
      }, { rootMargin: '-25% 0px -60% 0px' });
      spreads.forEach(function (s) { spy.observe(s); });
    }
  }

  function init(root) {
    if (!root || root.dataset.bqMtcInit) return;
    root.dataset.bqMtcInit = '1';

    // The manner is chosen once, normally by the section's pre-paint inline
    // stamp (honoured first, so a resize between paint and this deferred run
    // cannot split the page across two modes); by viewport as the fallback.
    // Re-binding a live reading on resize would tear the page out from under
    // the reader: a rotation mid-visit keeps the manner it arrived with.
    if (root.classList.contains('bq-mtc--roll')) initRoll(root);
    else if (root.classList.contains('bq-mtc--bound')) initCodex(root);
    else if (SINGLE.matches) initRoll(root);
    else initCodex(root);

    bq.wireNuwa(root, '[data-mtc-nuwa]');
  }

  document.querySelectorAll('.bq-mtc').forEach(init);
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('.bq-mtc').forEach(init);
  });
})();
